/**
 * Board Members Pinia store — manages the board member roster and pending invitations
 * for the current HOA tenant.
 *
 * All mutations go through actions; never mutate state outside this store.
 * API calls use the openapi-fetch client from @/api/client — never hand-roll fetch.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';
export const useBoardMembersStore = defineStore('boardMembers', () => {
    // ── State ─────────────────────────────────────────────────────────────────
    const members = ref([]);
    const invitations = ref([]);
    const activeTab = ref('all');
    const isLoading = ref(false);
    const error = ref(null);
    // ── Computed ──────────────────────────────────────────────────────────────
    const activeCount = computed(() => members.value.filter((m) => m.isActive).length);
    const smsCount = computed(() => members.value.filter((m) => {
        if (!m.notificationMethodsJson)
            return false;
        try {
            const methods = JSON.parse(m.notificationMethodsJson);
            return methods.includes('sms');
        }
        catch {
            return false;
        }
    }).length);
    const activeLastSevenDays = computed(() => {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return members.value.filter((m) => m.lastActiveAt !== null && m.lastActiveAt !== undefined && new Date(m.lastActiveAt) > sevenDaysAgo).length;
    });
    const pendingInviteCount = computed(() => invitations.value.filter((i) => i.status === 'Pending').length);
    // ── Actions ───────────────────────────────────────────────────────────────
    /**
     * Fetch board members for the given tab (defaults to activeTab).
     * Also fetches pending invitations when on the pending_invites tab or when tab is 'all'.
     */
    async function fetchMembers(tab) {
        const targetTab = tab ?? activeTab.value;
        if (tab)
            activeTab.value = tab;
        isLoading.value = true;
        error.value = null;
        try {
            const tabParam = targetTab === 'roles_permissions' ? 'all' : targetTab;
            const { data, error: apiError } = await api.GET('/v1/board/members', {
                params: { query: { tab: tabParam === 'pending_invites' ? 'pending_invites' : tabParam } },
            });
            if (apiError)
                throw new Error(JSON.stringify(apiError));
            if (targetTab === 'pending_invites') {
                // The endpoint returns invitations in this tab
                invitations.value = data ?? [];
                members.value = [];
            }
            else {
                members.value = data ?? [];
            }
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load board members.';
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Send a board member invitation.
     */
    async function invite(request) {
        const { error: apiError } = await api.POST('/v1/board/members/invite', {
            body: request,
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
        // Re-fetch to show the new pending invite
        await fetchMembers('pending_invites');
    }
    /**
     * Update a board member (role change, deactivate, or notification prefs).
     */
    async function updateMember(id, patch) {
        const { data, error: apiError } = await api.PATCH('/v1/board/members/{id}', {
            params: { path: { id } },
            body: patch,
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
        if (data) {
            const idx = members.value.findIndex((m) => m.id === id);
            if (idx !== -1)
                members.value[idx] = data;
        }
    }
    /**
     * Revoke a pending invitation.
     */
    async function revokeInvite(invitationId) {
        const { error: apiError } = await api.DELETE('/v1/board/members/invitations/{id}', {
            params: { path: { id: invitationId } },
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
        invitations.value = invitations.value.filter((i) => i.id !== invitationId);
    }
    /**
     * Resend a pending invitation email.
     */
    async function resendInvite(invitationId) {
        const { error: apiError } = await api.POST('/v1/board/members/invitations/{id}/resend', {
            params: { path: { id: invitationId } },
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
    }
    /**
     * Download the member list as CSV. Uses native fetch for blob download
     * (openapi-fetch doesn't support binary responses — this is the one allowed exception).
     */
    async function exportCsv() {
        const resp = await fetch('/v1/board/members/export', {
            headers: { Accept: 'text/csv' },
        });
        if (!resp.ok)
            throw new Error('Export failed');
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'board-members.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    function $reset() {
        members.value = [];
        invitations.value = [];
        activeTab.value = 'all';
        isLoading.value = false;
        error.value = null;
    }
    return {
        members,
        invitations,
        activeTab,
        isLoading,
        error,
        activeCount,
        smsCount,
        activeLastSevenDays,
        pendingInviteCount,
        fetchMembers,
        invite,
        updateMember,
        revokeInvite,
        resendInvite,
        exportCsv,
        $reset,
    };
});
